import { type } from 'arktype'

/**
 * The tariff segments of the RC Pro offer. Each activity belongs to one, which sets the special conditions, the
 * limits on offer, the management fee and the activity statement.
 */
export const frRcpaxeSegments = ['AUTO', 'BIE', 'COM', 'MAC', 'MAN', 'MED', 'MISC', 'SECU', 'TECH'] as const
export const frRcpaxeSegmentsSchema = type('===', ...frRcpaxeSegments)
export type FrRcpaxeSegment = typeof frRcpaxeSegmentsSchema.infer

export const frRcpaxeSubSegments = ['BORLOO'] as const
export const frRcpaxeSubSegmentsSchema = type('===', ...frRcpaxeSubSegments)
export type FrRcpaxeSubSegment = typeof frRcpaxeSubSegmentsSchema.infer

export const frRcpaxeActivityClauses = [
  'transportGoods',
  'courtierTravaux',
  'locationVehicules',
  'conseilDecoration',
  'salleDanse',
  'gardeAnimaux',
  'comiteEntreprise',
] as const
export const frRcpaxeActivityClausesSchema = type('===', ...frRcpaxeActivityClauses)
export type FrRcpaxeActivityClause = typeof frRcpaxeActivityClausesSchema.infer

/**
 * `tariffActivityLabel` is the activity's name in the RC Pro tariff, the label fr-rcpw sent to Wakam as `Activite_n`.
 * The tariff rates the activity through it.
 */
export const frRcpaxeActivityConfigurationSchema = type({
  tariffActivityLabel: 'string',
  segment: frRcpaxeSegmentsSchema,
  'subSegment?': frRcpaxeSubSegmentsSchema.or(type.null),
  'specificClauses?': frRcpaxeActivityClausesSchema.array().or(type.null),
})

export type FrRcpaxeActivityConfiguration = typeof frRcpaxeActivityConfigurationSchema.infer

/**
 * A subscription holds a single segment (activities of different segments are excluded), so the segment is the one of
 * the first activity.
 */
export function getFrRcpaxeSegmentFromActivitiesConfigurations(
  activitiesConfigurations: readonly FrRcpaxeActivityConfiguration[] | null | undefined,
): FrRcpaxeSegment | undefined {
  return activitiesConfigurations?.[0]?.segment
}

export function hasFrRcpaxeActivitySubSegment(
  activitiesConfigurations: readonly Pick<FrRcpaxeActivityConfiguration, 'subSegment'>[] | null | undefined,
  subSegment: FrRcpaxeSubSegment,
): boolean {
  return activitiesConfigurations?.some((configuration) => configuration.subSegment === subSegment) ?? false
}

export function getFrRcpaxeActivityClauses(
  activitiesConfigurations: readonly Pick<FrRcpaxeActivityConfiguration, 'specificClauses'>[] | null | undefined,
): FrRcpaxeActivityClause[] {
  const clauses = new Set<FrRcpaxeActivityClause>()
  for (const configuration of activitiesConfigurations ?? []) {
    for (const clause of configuration.specificClauses ?? []) clauses.add(clause)
  }
  return [...clauses]
}
